import React, { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { usePOSStore, type POSStep } from '../../stores/pos.store';
import { ShoppingBag, ChevronUp, Sparkles, Wallet, Check, Calendar, ChevronDown, ArrowRight, Minus } from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { CustomerSidebar } from '../common/CustomerSidebar';
import { POSCartSummarySidebar } from './POSCartSummarySidebar';
import { POSCartStep } from './steps/POSCartStep';
import { POSServiceSelector } from './steps/POSServiceSelector';
import { POSTipStep } from './steps/POSTipStep';
import { POSPaymentStep } from './steps/POSPaymentStep';
import { POSSummaryStep } from './steps/POSSummaryStep';
import { useBaseCurrency, useActiveRate } from '../../db/hooks/useLookupData';
import { DateTimePicker } from '../common/DateTimePicker';

export function POSDrawer() {
  const {
    isOpen,
    close,
    step,
    isSelectingService,
    editingCartItemId,
    setStep,
    cartItems,
    customer,
    setCustomer,
    setIsCustomerSearchOpen,
    discount,
    tip,
    paymentEntries,
    operationalDate,
    setOperationalDate,
  } = usePOSStore();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { activeRate } = useActiveRate();
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [lastTransactionId, setLastTransactionId] = useState<string | null>(null);

  if (typeof window !== 'undefined') {
    (window as any).__posStore = usePOSStore;
  }

  // State to hold save functions from POSPaymentStep
  const [onSaveTransaction, setOnSaveTransaction] = useState<{
    onPaid?: (() => void) | null;
    onPending?: (() => void) | null;
    isSaving?: boolean;
    isComplete?: boolean;
    lastTransactionId?: string | null;
  }>({});

  const handleSaveStateChange = (state: {
    onPaid?: (() => void) | null;
    onPending?: (() => void) | null;
    isComplete: boolean;
    isSaving: boolean;
    lastTransactionId?: string | null;
  }) => {
    setOnSaveTransaction(state);
    if (state.lastTransactionId) {
      setLastTransactionId(state.lastTransactionId);
    }
  };

  if (!isOpen) return null;

  const rateKey = baseCurrency.toLowerCase() as 'usd' | 'eur' | 'ves';
  const exchangeRateValue = rateKey === 'eur' ? (activeRate?.eur || 1) : rateKey === 'usd' ? (activeRate?.usd || 1) : 1;

  const subtotal = cartItems.reduce(
    (acc, item) => acc + Math.max(0, item.unitPrice - (item.discountAmount || 0)) * item.quantity,
    0
  );
  const totalPaid = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);
  const netPayable = subtotal - discount + tip;
  const pending = netPayable - totalPaid;

  const getStepTitle = () => {
    switch (step) {
      case 'cart':
        if (editingCartItemId) return 'Editar Servicio';
        return isSelectingService ? 'Añadir al Carrito' : 'Punto de Venta';
      case 'tips':
        return 'Propinas & Atribución';
      case 'payment':
        return 'Seleccionar Pago';
      case 'summary':
        return 'Venta Completada';
      default:
        return 'Punto de Venta';
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 'cart':
        return ShoppingBag;
      case 'tips':
        return Sparkles;
      case 'payment':
        return Wallet;
      case 'summary':
        return Check;
      default:
        return ShoppingBag;
    }
  };

  const canNavigateTo = (targetStep: POSStep) => {
    const stepOrder: Record<POSStep, number> = { cart: 1, tips: 2, payment: 3, summary: 4 };
    const currentOrder = stepOrder[step];
    const targetOrder = stepOrder[targetStep];
    if (targetOrder <= currentOrder) return true;
    if (cartItems.length === 0) return false;
    if (cartItems.some((i) => !i.operatorId)) return false;
    const hasPackages = cartItems.some((i) => i.type === 'package' || Boolean(i.package) || Boolean(i.selectedTier));
    if (hasPackages && !customer) return false;
    return true;
  };

  // ── Build desktop sidebar ──
  const buildSidebar = (): { sidebar: React.ReactNode | undefined; position: 'left' | 'right' } => {
    if (step === 'cart' && !isSelectingService) {
      const contextCards = (
        <div className="bg-surface-secondary backdrop-blur-md border border-border p-4 rounded-2xl shadow-xs space-y-3 text-xs">
          <div className="flex items-center gap-2.5 text-text-secondary">
            <Wallet className="w-4 h-4 text-brand-sky shrink-0" />
            <span className="font-semibold">Caja Abierta · POS</span>
          </div>
          <div className="flex items-center gap-2.5 text-text-secondary border-t border-border/60 pt-2">
            <Sparkles className="w-4 h-4 text-brand-orange shrink-0" />
            <span className="font-semibold">Sede Activa</span>
          </div>
        </div>
      );

      return {
        sidebar: (
          <CustomerSidebar
            variant="compact"
            customer={customer}
            onSearchClient={() => setIsCustomerSearchOpen(true)}
            onRemoveClient={() => setCustomer(null)}
            contextCards={contextCards}
          />
        ),
        position: 'left',
      };
    }

    if (step === 'cart' && isSelectingService) {
      return {
        sidebar: (
          <POSCartSummarySidebar
            primaryAction={{
              label: 'Ver Carrito Detallado',
              onClick: () => usePOSStore.getState().setIsSelectingService(false),
            }}
          />
        ),
        position: 'right',
      };
    }

    if (step === 'tips') {
      return {
        sidebar: (
          <POSCartSummarySidebar
            primaryAction={{
              label: 'Continuar con el pago',
              onClick: () => setStep('payment'),
            }}
          />
        ),
        position: 'right',
      };
    }

    if (step === 'payment') {
      return {
        sidebar: (
          <POSCartSummarySidebar
            primaryAction={
              onSaveTransaction.isComplete
                ? {
                    label: onSaveTransaction.isSaving ? 'Procesando...' : 'Finalizar Venta',
                    onClick: () => onSaveTransaction.onPaid?.(),
                    disabled: onSaveTransaction.isSaving,
                  }
                : undefined
            }
            secondaryAction={{
              label: 'Guardar venta pendiente',
              onClick: () => onSaveTransaction.onPending?.(),
              disabled: onSaveTransaction.isSaving,
            }}
          />
        ),
        position: 'right',
      };
    }

    return { sidebar: undefined, position: 'left' };
  };

  const { sidebar, position } = buildSidebar();

  return (
    <Drawer
      isOpen={isOpen}
      onClose={close}
      headerActions={
        <button
          type="button"
          onClick={close}
          title="Minimizar venta (mantener borrador)"
          aria-label="Minimizar venta"
          className="btn-press rounded-xl p-2 sm:p-2.5 min-h-[40px] min-w-[40px] sm:min-h-[44px] sm:min-w-[44px] flex items-center justify-center text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer shrink-0"
        >
          <Minus className="h-5 w-5" />
        </button>
      }
      title={
        <div className="flex items-center gap-2.5 flex-wrap">
          <span>{getStepTitle()}</span>
          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="flex items-center gap-1.5 text-xs font-bold text-text-secondary hover:text-brand-sky transition-colors cursor-pointer bg-surface-secondary/50 border border-border rounded-lg px-2.5 py-1"
          >
            <Calendar className="w-3.5 h-3.5 text-brand-sky" />
            <span className="capitalize">{dayjs(operationalDate).locale('es').format('ddd D MMM, HH:mm')}</span>
            <ChevronDown className="w-3 h-3 text-text-tertiary" />
          </button>
        </div>
      }
      subtitle={step === 'cart' ? 'Agrega services, productos y packages' : undefined}
      icon={getStepIcon()}
      maxWidth="5xl"
      noPadding={!sidebar}
      sidebar={sidebar}
      sidebarPosition={position}
    >
      {/* Breadcrumb Navigation — Visible across Carrito, Propina and Pago steps */}
      {sidebar && step !== 'summary' && !editingCartItemId && (
        <div className="flex items-center gap-1.5 text-xs text-text-secondary mb-2.5 px-3.5 sm:px-6 pt-1">
          <button 
            onClick={() => setStep('cart')} 
            className={`transition-colors cursor-pointer ${step === 'cart' ? 'text-brand-orange font-extrabold' : 'font-semibold hover:text-text-primary'}`}
          >
            Carrito
          </button>
          <span className="text-text-tertiary">›</span>
          <button
            onClick={() => canNavigateTo('tips') && setStep('tips')}
            className={`transition-colors cursor-pointer ${step === 'tips' ? 'text-brand-orange font-extrabold' : 'font-semibold hover:text-text-primary'}`}
          >
            Propina
          </button>
          <span className="text-text-tertiary">›</span>
          <button
            onClick={() => canNavigateTo('payment') && setStep('payment')}
            className={`transition-colors cursor-pointer ${step === 'payment' ? 'text-brand-orange font-extrabold' : 'font-semibold hover:text-text-primary'}`}
          >
            Pago
          </button>
        </div>
      )}

      {/* Main Step Content Area — with responsive bottom space only when sticky bar is active */}
      <div className={`h-full flex flex-col overflow-hidden ${sidebar && step !== 'summary' && !editingCartItemId ? 'pb-20 lg:pb-0' : ''}`}>
        {step === 'cart' && (isSelectingService ? <POSServiceSelector /> : <POSCartStep />)}
        {step === 'tips' && <POSTipStep />}
        {step === 'payment' && <POSPaymentStep onSaveStateChange={handleSaveStateChange} />}
        {step === 'summary' && <POSSummaryStep lastTransactionId={lastTransactionId} />}
      </div>

      {/* ── Mobile Backdrop for Expanded Bottom Sheet ── */}
      {isMobileSummaryOpen && !editingCartItemId && (
        <div 
          className="lg:hidden fixed inset-0 z-[74] bg-black/40 backdrop-blur-xs animate-fade-in"
          onClick={() => setIsMobileSummaryOpen(false)}
        />
      )}

      {/* ── Mobile & Tablet Sticky Action Bar & Bottom Sheet (< 1024px) ── */}
      {sidebar && step !== 'summary' && !editingCartItemId && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[75] bg-surface-secondary/95 backdrop-blur-xl border-t border-border p-3 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] shadow-2xl flex flex-col gap-2 rounded-t-3xl transition-all">
          
          {/* Main Sticky Bar Header */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
              className="flex items-center gap-2 text-left cursor-pointer group"
            >
              <div className="w-8 h-8 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange font-bold shrink-0">
                {cartItems.length}
              </div>
              <div>
                <span className="text-xs font-black text-text-primary block leading-tight group-hover:text-brand-orange transition-colors flex items-center gap-1">
                  {currencySymbol}{Math.max(0, pending).toFixed(2)} {baseCurrency}
                  <ChevronUp className={`w-3.5 h-3.5 transition-transform ${isMobileSummaryOpen ? 'rotate-180' : ''}`} />
                </span>
                <span className="text-[10px] font-bold text-text-tertiary block">
                  {customer?.name || 'Cliente Mostrador'} • Ver desglose
                </span>
              </div>
            </button>

            {/* Mobile Primary Action Button — Single instance in sticky bar */}
            {step === 'cart' && !isSelectingService && (
              <button
                onClick={() => canNavigateTo('tips') && setStep('tips')}
                disabled={cartItems.length === 0}
                className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold px-4 py-2.5 rounded-xl text-xs shadow-md shadow-brand-orange/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                Continuar <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}

            {step === 'cart' && isSelectingService && (
              <button
                onClick={() => usePOSStore.getState().setIsSelectingService(false)}
                className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold px-4 py-2.5 rounded-xl text-xs shadow-md shadow-brand-orange/20 cursor-pointer"
              >
                Ver Carrito
              </button>
            )}

            {step === 'tips' && (
              <button
                onClick={() => setStep('payment')}
                className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold px-4 py-2.5 rounded-xl text-xs shadow-md shadow-brand-orange/20 cursor-pointer flex items-center gap-1.5"
              >
                Pagar <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}

            {step === 'payment' && onSaveTransaction.isComplete && (
              <button
                onClick={() => onSaveTransaction.onPaid?.()}
                disabled={onSaveTransaction.isSaving}
                className="bg-brand-orange hover:bg-brand-orange/90 text-white font-black px-4 py-2.5 rounded-xl text-xs shadow-md shadow-brand-orange/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                {onSaveTransaction.isSaving ? (
                  'Procesando...'
                ) : (
                  <>
                    Finalizar Venta <Sparkles className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* Mobile Bottom Sheet Slide-Up Detail (Without duplicate primary button) */}
          {isMobileSummaryOpen && (
            <div className="mt-2 pt-3 border-t border-border/80 animate-slide-up space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <POSCartSummarySidebar
                primaryAction={undefined}
                secondaryAction={
                  step === 'payment'
                    ? {
                        label: 'Guardar venta pendiente',
                        onClick: () => onSaveTransaction.onPending?.(),
                        disabled: onSaveTransaction.isSaving,
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      )}

      {isDatePickerOpen && (
        <DateTimePicker
          value={operationalDate}
          onChange={(iso) => setOperationalDate(iso)}
          onClose={() => setIsDatePickerOpen(false)}
        />
      )}
    </Drawer>
  );
}
